// Cool, muted data colors keep dense charts legible without fighting the
// product's green interaction color.
const COLORS_LIGHT = [
  "#247a73", // teal
  "#466fa6", // blue
  "#c18432", // amber
  "#75649a", // violet
  "#b85f67", // coral
  "#3e8490", // cyan
  "#718956", // moss
  "#b66f43", // terracotta
  "#5c72a3", // indigo
  "#9b6688", // mauve
  "#8d7c42", // olive
  "#697982", // slate
];

// Dark-mode counterparts preserve the same families at higher luminance.
const COLORS_DARK = [
  "#56b9ad", // teal
  "#7ea6d9", // blue
  "#deb064", // amber
  "#a99bd0", // violet
  "#dc8990", // coral
  "#72bdc5", // cyan
  "#9db17f", // moss
  "#d99b74", // terracotta
  "#93a6d2", // indigo
  "#c594b3", // mauve
  "#b6a86c", // olive
  "#9ba8ae", // slate
];

type ThemeColors = {
  color: string;
  secondary: string;
  border: string;
  lightBorder: string;
  bgElevated: string;
  primary: string;
  combo: string;
};

type DashboardColors = {
  emissions: string;
  scope: Record<number, string>;
  target: string;
};

const DASHBOARD_COLORS: Record<"light" | "dark", DashboardColors> = {
  light: {
    emissions: "#247a73",
    scope: { 1: "#247a73", 2: "#466fa6", 3: "#75649a" },
    target: "#75649a",
  },
  dark: {
    emissions: "#56b9ad",
    scope: { 1: "#56b9ad", 2: "#7ea6d9", 3: "#a99bd0" },
    target: "#a99bd0",
  },
};

const THEME_COLORS: Record<string, ThemeColors> = {
  light: {
    color: "#18201e",
    secondary: "#65706c",
    border: "#dde3e0",
    lightBorder: "#e8ecea",
    bgElevated: "#ffffff",
    primary: "#16824d",
    combo: "#2276ad",
  },
  dark: {
    color: "#edf2f0",
    secondary: "#a8b1ae",
    border: "#353d3b",
    lightBorder: "#2a3130",
    bgElevated: "#191d1e",
    primary: "#43c77d",
    combo: "#6caee3",
  },
};

function readCssVar(name: string): string | undefined {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || undefined;
}

let fontFamilyCache: string | undefined;

// ECharts renders text on <canvas>, which does not inherit the page font.
// Resolve the effective body font family once so chart text matches the app.
export function getChartFontFamily(): string {
  if (!fontFamilyCache) {
    fontFamilyCache =
      getComputedStyle(document.body).fontFamily ||
      "'Geist Variable', sans-serif";
  }
  return fontFamilyCache;
}

// Design tokens in main.css are hex values, so chart colors can be read
// straight from the CSS custom properties (ECharts receives hex directly).
function readCssColor(name: string): string | undefined {
  return readCssVar(name);
}

type ThemeColorCacheEntry = {
  palette: string[];
  theme: ThemeColors;
  domDark: boolean;
};

const themeColorCache = new Map<boolean, ThemeColorCacheEntry>();

function resolveThemeColors(isDark: boolean): ThemeColorCacheEntry {
  // theme-init.js applies the persisted theme class to <html> before React
  // mounts, but the zustand theme store rehydrates asynchronously — so on the
  // first render the `isDark` prop can disagree with the CSS variables that
  // getComputedStyle resolves. A cache keyed only on `isDark` would then
  // permanently store one theme's colors under the other theme's key (e.g.
  // dark colors cached as "light" after a persisted dark load), leaving chart
  // colors and text stuck on the wrong theme after a toggle. Recompute
  // whenever the DOM theme differs from the one the entry was read under.
  const domDark =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");

  const cached = themeColorCache.get(isDark);
  if (cached && cached.domDark === domDark) return cached;

  const fallback = isDark ? THEME_COLORS.dark : THEME_COLORS.light;
  const palette = isDark ? COLORS_DARK : COLORS_LIGHT;

  const result: ThemeColorCacheEntry = {
    palette,
    theme: {
      color: readCssColor("--foreground") ?? fallback.color,
      secondary: readCssColor("--muted-foreground") ?? fallback.secondary,
      border: readCssColor("--border") ?? fallback.border,
      lightBorder: readCssColor("--chart-grid") ?? fallback.lightBorder,
      bgElevated: readCssColor("--card") ?? fallback.bgElevated,
      primary: readCssColor("--primary") ?? fallback.primary,
      combo: readCssColor("--info") ?? fallback.combo,
    },
    domDark,
  };

  themeColorCache.set(isDark, result);
  return result;
}

export function readThemeColors(isDark: boolean): {
  palette: string[];
  theme: ThemeColors;
} {
  const { palette, theme } = resolveThemeColors(isDark);
  return { palette, theme };
}

export type { ThemeColors };

// Returns `count` theme-consistent colors for charts with many items/series.
export function getChartPalette(isDark: boolean, count: number): string[] {
  if (count <= 0) return [];
  const fallback = isDark ? COLORS_DARK : COLORS_LIGHT;
  return Array.from({ length: count }, (_, i) => fallback[i % fallback.length]);
}

export function getDashboardColors(isDark: boolean): DashboardColors {
  return DASHBOARD_COLORS[isDark ? "dark" : "light"];
}
